<script>
  import { onMount } from "svelte";
  import { ComboBox } from "carbon-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import GlobalAnonRuleEditor from "./GlobalAnonRuleEditor.svelte";

  // "windowtitles" or "elements"
  export let kind = "windowtitles";
  // Loaded rules of the selected project, bound by the parent for the CSV export.
  export let rules = [];
  export let ready = false;

  // Project rules live in the rule set of the TASK_REGISTRATION stage (2),
  // the stage the anonymization pipeline reads.
  const PIPELINE_STAGE = 2;

  let projectOptions = [];
  let selectedProject = null;
  let loading = false;
  let message = "";
  let ruleSetId = null;

  // Both rule types are edited through the existing project rule endpoints.
  $: persist = ruleSetId ? { save: saveRule, remove: removeRule } : null;
  $: ready = Boolean(selectedProject && persist && !loading && !message);

  $: loadRules(selectedProject);

  onMount(async () => {
    const response = await fetchWithSession("/api/damprojects/projects");
    if (!response.ok) return;
    const data = await response.json();
    projectOptions = [...(data.running || []), ...(data.analysis || [])]
      .map((project) => ({ id: String(project.id), text: project.name || String(project.id) }))
      .sort((a, b) => a.text.localeCompare(b.text));
  });

  async function loadRules(projectId) {
    rules = [];
    message = "";
    ruleSetId = null;
    if (!projectId) return;
    loading = true;
    try {
      const ruleSetResponse = await fetchWithSession(
        `/api/damprojects/getProjectAnonymizations/${projectId}/${PIPELINE_STAGE}`,
        { suppressErrorToast: true },
      );
      if (!ruleSetResponse.ok) {
        message = "This project has no rule set yet.";
        return;
      }
      const loadedRuleSetId = await ruleSetResponse.json();
      const response = await fetchWithSession(
        kind === "windowtitles"
          ? `/api/damprojects/DAMAnonymizationRules/${loadedRuleSetId}`
          : `/api/damprojects/DAMAnonymizationRulesUI/${loadedRuleSetId}`,
      );
      if (!response.ok) return;
      const data = await response.json();
      // Only reload results for the project that is still selected.
      if (projectId !== selectedProject) return;
      ruleSetId = loadedRuleSetId;
      rules = (Array.isArray(data) ? data : data.items || []).map((rule) => ({
        ...rule,
        id: String(rule.id),
        name: rule.name || "",
      }));
    } finally {
      loading = false;
    }
  }

  async function saveRule(rule) {
    const common = {
      id: rule.id || 0,
      name: rule.name,
      applicationname: rule.applicationname || "",
      idanonymizationrules: ruleSetId,
    };
    const response = await fetchWithSession(
      kind === "windowtitles"
        ? "/api/damprojects/saveDAMAnonymizationRule?preset=false"
        : "/api/damprojects/saveDAMAnonymizationRuleUI",
      {
        method: "POST",
        body: JSON.stringify(
          kind === "windowtitles"
            ? { ...common, search: rule.search, replace: rule.replace }
            : {
                ...common,
                namesearch: rule.namesearch || "",
                namereplace: rule.namereplace || "",
                automationsearch: rule.automationsearch || "",
                automationreplace: rule.automationreplace || "",
                elementclass: rule.elementclass || "*",
                type: rule.type || "*",
              },
        ),
      },
    );
    if (!response.ok) return null;
    const data = await response.json();
    return { ...rule, id: String(data.id) };
  }

  async function removeRule(rule) {
    const response = await fetchWithSession(
      kind === "windowtitles"
        ? `/api/damprojects/deleteDAMAnonymizationRule/${rule.id}?preset=false`
        : `/api/damprojects/deleteDAMAnonymizationRuleUI/${rule.id}`,
      { method: "POST" },
    );
    return response.ok;
  }

  function shouldFilterItem(item, value) {
    return item.text.toLowerCase().includes(value.toLowerCase());
  }
</script>

<div class="project-rules">
  <div class="project-filter">
    <ComboBox
      id={`project-rules-${kind}`}
      titleText="Project"
      placeholder="Select a project"
      items={projectOptions}
      bind:selectedId={selectedProject}
      {shouldFilterItem}
    />
  </div>
  {#if !selectedProject}
    <p class="hint">Select a project to see its rules.</p>
  {:else if loading}
    <p class="hint">Loading rules...</p>
  {:else if message}
    <p class="hint">{message}</p>
  {:else}
    <GlobalAnonRuleEditor {kind} bind:rules readonly={!persist} {persist} />
  {/if}
</div>

<style>
  .project-rules {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
    min-width: 0;
  }
  .project-filter {
    max-width: 24rem;
  }
  /* Same as the editor filters: the input draws the border, the list box none. */
  .project-filter :global(.bx--combo-box.bx--list-box) {
    border: 0 !important;
    background: transparent;
  }
  .hint {
    margin: 0;
    color: var(--pax-sub);
    font-size: 0.875rem;
  }
</style>
